import Push from 'push.js';
import { getFromLocalStorageAndDecrypt } from '../../../Components/WebChat/WebChatEncryptDecrypt';
import { getChannelInfo } from '../../../slice';
import { Store } from '../../../store';
import { logo } from '../../../Helpers/Base64';
import logger from '../../../Helpers/logger';

/**
 * Notification type constants
 */
export const NOTIFICATION_TYPES = {
    MESSAGE: 'MESSAGE',
    REQUEST_ACCEPTED: 'REQUEST_ACCEPTED',
    REQUEST_DECLINED: 'REQUEST_DECLINED',
    NEW_JOIN_REQUEST: 'NEW_JOIN_REQUEST',
};

export const handleMessageParseHtml = (msg = '') => {
    const parseResult = new DOMParser().parseFromString(msg, 'text/html');
    return parseResult?.documentElement.textContent;
};

/**
 * Channel-specific notification service
 * Handles all notification logic for channel messages
 */

/**
 * Get channel name from various possible sources
 * @param {Object} channelInfo - Channel info object
 * @param {Object} data - Alternative data object
 * @returns {string} Channel name
 */
const getChannelName = (channelInfo = {}, data = {}) => {
    return channelInfo?.channelName || channelInfo?.name || data?.channelName || data?.name || 'Unknown Channel';
};

/**
 * Notification template functions
 * These functions generate title and body based on provided data
 */
const NOTIFICATION_TEMPLATES = {
    [NOTIFICATION_TYPES.MESSAGE]: data => {
        const channelName = getChannelName({}, data);
        return {
            title: channelName,
            body: `You have new messages from ${channelName}`,
        };
    },
    [NOTIFICATION_TYPES.REQUEST_ACCEPTED]: data => {
        const channelName = getChannelName({}, data);
        return {
            title: 'Request Accepted',
            body: `Your request to join ${channelName} has been approved.`,
        };
    },
    [NOTIFICATION_TYPES.REQUEST_DECLINED]: data => {
        const channelName = getChannelName({}, data);
        return {
            title: 'Request Declined',
            body: `Your request to join ${channelName} has been declined.`,
        };
    },
    [NOTIFICATION_TYPES.NEW_JOIN_REQUEST]: data => {
        const channelName = getChannelName({}, data);
        const username = data?.username || data?.userName || data?.initiatedBy?.name || 'Someone';
        return {
            title: 'New Join Request',
            body: `${username} requested to join ${channelName}`,
        };
    },
};

/**
 * Get channel info by channel ID or from data
 * @param {string} channelId - Channel ID
 * @param {Object} data - Alternative data object
 * @returns {Object} Channel info
 */
const getChannelInfoById = (channelId, data = {}) => {
    return getChannelInfo(channelId) || data?.channel || {};
};

/**
 * Get channel image from various possible sources
 * @param {Object} channelInfo - Channel info object
 * @param {Object} data - Alternative data object
 * @returns {string} Channel image URL
 */

/**
 * Helper function to format notification log message
 * @param {string} prefix - Message prefix with emoji
 * @param {string} action - Action name (shown, error, closed)
 * @param {string|null} notificationType - Optional notification type
 * @returns {string} Formatted message
 */
const formatNotificationLogMessage = (prefix, action, notificationType) => {
    const baseMessage = prefix + ' Notification ' + action;
    if (notificationType) {
        return baseMessage + ': ' + notificationType;
    }
    return baseMessage;
};

/**
 * Create default notification options
 * @param {Object} config - Configuration object
 * @param {string} config.body - Notification body text
 * @param {string} config.channelId - Channel ID
 * @param {string} config.channelImage - Channel image URL
 * @param {string} config.notificationType - Optional notification type
 * @param {Function} config.onClick - Optional custom onClick handler
 * @param {Function} config.onShow - Optional custom onShow handler
 * @param {Function} config.onError - Optional custom onError handler
 * @param {Function} config.onClose - Optional custom onClose handler
 * @returns {Object} Notification options
 */
const createNotificationOptions = ({
    body,
    channelId,
    channelImage = '',
    notificationType = null,
    onClick = null,
    onShow = null,
    onError = null,
    onClose = null,
}) => {
    return {
        body: handleMessageParseHtml(body),
        icon: channelImage,
        tag: JSON.stringify(notificationType ? { channelId, type: notificationType } : { channelId }),
        requireInteraction: true,
        timeout: 8000,
        onClick: onClick,
        onShow:
            onShow ||
            function () {
                logger.debug(formatNotificationLogMessage('✅', 'shown', notificationType));
            },
        onError:
            onError ||
            function (e) {
                logger.debug(formatNotificationLogMessage('❌', 'error', notificationType), e);
            },
        onClose:
            onClose ||
            function () {
                logger.debug(formatNotificationLogMessage('📴', 'closed', notificationType));
            },
    };
};

/**
 * Detect operating system from user agent
 * @returns {string|null} OS name or null
 */
const detectOS = () => {
    try {
        const userAgent = window.navigator.userAgent.toLowerCase();
        const macosPlatforms = /(macintosh|macintel|macppc|mac68k|macos)/i;
        const windowsPlatforms = /(win32|win64|windows|wince)/i;
        const iosPlatforms = /(iphone|ipad|ipod)/i;

        if (macosPlatforms.test(userAgent)) return 'macos';
        if (iosPlatforms.test(userAgent)) return 'ios';
        if (windowsPlatforms.test(userAgent)) return 'windows';
        if (/android/.test(userAgent)) return 'android';
        if (/linux/.test(userAgent)) return 'linux';

        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Check if browser supports notifications and has permission
 * @returns {boolean} True if notifications are available
 */
const checkBrowserNotificationSupport = () => {
    return 'Notification' in window && Push.Permission.has();
};

/**
 * Check if channel notifications are enabled and user has permission
 * @returns {boolean} True if notifications are allowed
 */
export const checkChannelNotificationPermission = () => {
    try {
        const webSettings = getFromLocalStorageAndDecrypt('websettings');
        const parseredWebSettings = webSettings ? JSON.parse(webSettings) : {};
        const { Notifications = true } = parseredWebSettings;
        const hasPushPermission = Push.Permission.has();

        return Notifications && hasPushPermission;
    } catch (error) {
        logger.debug('❌ Failed to check channel notification permission:', error);
        return false;
    }
};

/**
 * Check if the current user is viewing the specified channel
 * @param {string} channelId - The channel ID to check
 * @returns {boolean} True if user is currently viewing the channel
 */
const isUserViewingChannel = channelId => {
    try {
        const activeChannel = Store.getState().channelNavData?.activeConversationChannel;
        const isViewing = activeChannel?.channelId === channelId;

        return isViewing;
    } catch (error) {
        logger.debug('❌ Failed to check if user is viewing channel:', error);
        return false;
    }
};

/**
 * Check if the page is currently visible
 * @returns {boolean} True if page is visible
 */
const isPageVisible = () => {
    try {
        const browserTab = Store.getState().browserTabData;
        const isVisible = browserTab?.isVisible || false;
        return isVisible;
    } catch (error) {
        logger.debug('❌ Failed to check page visibility:', error);
        return true; // Default to visible if check fails
    }
};

/**
 * Get the current user ID
 * @returns {string|null} Current user ID or null
 */
const getCurrentUserId = () => {
    try {
        const userId = Store.getState().vCardData?.data?.userId || null;
        return userId;
    } catch (error) {
        logger.debug('❌ Failed to get current user ID:', error);
        return null;
    }
};

/**
 * Validate channel event notification conditions (generic for all notification types)
 * @param {string} channelId - The channel ID
 * @param {Object} channelInfo - The channel information
 * @param {string} notificationType - The notification type
 * @param {Object} data - The data object (for message senderId check)
 * @returns {Object} Validation result with isValid and reason
 */
const validateChannelEventNotificationConditions = (channelId, channelInfo, notificationType, data = {}) => {
    // For MESSAGE notifications, check if message is from current user
    if (notificationType === NOTIFICATION_TYPES.MESSAGE) {
        const currentUser = getCurrentUserId();
        const senderId = data?.senderId || data?.messageData?.senderId;
        if (senderId === currentUser) {
            return { isValid: false, reason: 'Message from current user' };
        }
    }

    // Check if channel is muted
    if (channelInfo?.isMuted) {
        return { isValid: false, reason: 'Channel is muted' };
    }

    const isViewingChannel = isUserViewingChannel(channelId);
    const isPageVisibleValue = isPageVisible();

    if (isPageVisibleValue && isViewingChannel) {
        return { isValid: false, reason: 'User is viewing channel and page is visible' };
    }

    return { isValid: true, reason: 'Notification allowed' };
};

/**
 * Send channel notification using the existing notification system
 * @param {string} title - The notification title
 * @param {string} imageUrl - The channel image URL
 * @param {string} messageBody - The message content
 * @param {string} channelId - The channel ID
 * @param {Object} customOptions - Additional options to override defaults
 */
const sendChannelPushNotification = ({ title, messageBody = '', channelId, customOptions = {} }) => {
    try {
        if (!checkBrowserNotificationSupport()) {
            logger.debug(
                '❌ [sendChannelPushNotification] Browser does not support notifications or permission denied',
            );
            return;
        }

        // If customOptions already includes onClick and other handlers, use them directly
        // Otherwise create default options
        let finalOptions;
        if (customOptions && Object.keys(customOptions).length > 0 && customOptions.onClick) {
            // Use custom options as-is since they're already complete
            finalOptions = customOptions;
        } else {
            // Create default options if no custom options provided
            const defaultOptions = createNotificationOptions({
                body: messageBody,
                channelId,
                icon: logo,
            });
            finalOptions = { ...defaultOptions, ...customOptions };
        }

        Push.create(title ? title.toString() : '', finalOptions);

        // Windows-specific handling
        const os = detectOS();
        if (os === 'windows') {
            const windowisChrome = (!window.chrome.webstore || !window.chrome.runtime) && !window.chrome;
            return windowisChrome ? true : undefined;
        }

        return true;
    } catch (error) {
        logger.debug('❌ [sendChannelPushNotification] Error :>> ', error);
        logger.error('[sendChannelPushNotification] Error details:', error.stack);
    }
};

/**
 * Send a configurable channel event notification
 * @param {string} notificationType - The type of notification (from NOTIFICATION_TYPES)
 * @param {Object} data - Data object containing channel information and other relevant data
 * @param {Object} options - Optional configuration for the notification
 * @param {boolean} options.skipPermissionCheck - Skip permission check (default: false)
 * @param {boolean} options.skipValidation - Skip validation checks (default: false)
 * @param {Function} options.onClick - Custom onClick handler
 * @param {Function} options.onShow - Custom onShow handler
 * @param {Function} options.onError - Custom onError handler
 * @param {Function} options.onClose - Custom onClose handler
 * @param {string} options.channelId - Channel ID (required)
 */
export const sendChannelEventNotification = (notificationType, data = {}, options = {}) => {
    try {
        if (!NOTIFICATION_TYPES[notificationType]) {
            logger.warn(`❌ [sendChannelEventNotification] Invalid notification type: ${notificationType}`);
            return;
        }

        if (!options.skipPermissionCheck && !checkChannelNotificationPermission()) {
            logger.debug('❌ [sendChannelEventNotification] Notification stopped: Permission check failed');
            return;
        }

        const template = NOTIFICATION_TEMPLATES[notificationType];
        if (!template) {
            logger.warn(`❌ [sendChannelEventNotification] No template found for type: ${notificationType}`);
            return;
        }

        const { title, body } = template(data);
        const channelId = options.channelId || data?.channelId || data?.channel?.channelId;

        if (!channelId) {
            logger.warn('❌ [sendChannelEventNotification] Channel ID is required');
            return;
        }

        // Get channel info for validation
        const channelInfo = getChannelInfoById(channelId, data);

        const validation = validateChannelEventNotificationConditions(channelId, channelInfo, notificationType, data);
        if (!validation.isValid) {
            logger.debug(
                `❌ [sendChannelEventNotification] ${notificationType} notification stopped: ${validation.reason}`,
            );
            return;
        }

        // Create onClick handler that wraps the custom onClick
        const notificationOnClick = options.onClick
            ? e => {
                  window.focus();
                  options.onClick(e);
                  Push.clear();
              }
            : null;

        const notificationOptions = createNotificationOptions({
            body,
            channelId,
            notificationType,
            onClick: notificationOnClick,
            onShow: options.onShow,
            onError: options.onError,
            onClose: options.onClose,
        });

        sendChannelPushNotification({
            title,
            messageBody: body,
            channelId,
            customOptions: notificationOptions,
        });

        return true;
    } catch (error) {
        logger.debug(`❌ [sendChannelEventNotification] Failed to send ${notificationType} notification:`, error);
        logger.error('[sendChannelEventNotification] Error details:', error.stack);
    }
};
